import {Control} from "ol/control"

// Pin order, not a fixed set: a user's available groups are sorted preference-first,
// then label-map (canonical) order — the top PINNED_COUNT become row toggles.
const PINNED_PREFERENCE = ["fw", "rk", "asb", "nah"]
const PINNED_COUNT = 4

// The single top-left control card: search rows are wired via Stimulus actions; this
// control builds all filter toggles from groups discovered in device payloads — the
// snapshot and streams are permission-scoped, so the payloads are the source of truth.
export class DeckControl extends Control {
    #mapController
    #labels
    #groups = new Set()

    constructor(element, mapController, labels) {
        element.classList.add("ol-unselectable", "ol-control")
        super({element: element})
        this.#mapController = mapController
        this.#labels = labels || {}

        this.filtersRow     = element.querySelector(".tracker-deck-filters")
        this.overflowButton = element.querySelector(".tracker-overflow-button")
        this.overflowCount  = element.querySelector(".tracker-overflow-count")
        this.overflowBadge  = element.querySelector(".tracker-overflow-badge")
        this.overflowPanel  = element.querySelector(".tracker-deck-overflow")
        this.overflowRows   = element.querySelector(".tracker-deck-overflow-rows")

        this.overflowButton.addEventListener("click", () => this.overflowPanel.classList.toggle("d-none"))
        element.querySelectorAll("[data-overflow-bulk]").forEach(button => {
            button.addEventListener("click", e => this.#bulkSet(e.currentTarget.dataset.overflowBulk === "all"))
        })
    }

    // Called for every group discovered from a device payload. Idempotent; re-places
    // all toggles so a later-discovered higher-preference group can claim a pinned slot.
    addGroup(group) {
        if (this.#groups.has(group)) return
        this.#groups.add(group)
        this.#render()
    }

    #render() {
        const sorted   = this.#sortedGroups()
        const pinned   = sorted.slice(0, PINNED_COUNT)
        const overflow = sorted.slice(PINNED_COUNT)

        this.filtersRow.querySelectorAll("[data-pinned]").forEach(el => el.remove())
        pinned.forEach(group => this.filtersRow.insertBefore(this.#pinnedToggle(group), this.overflowButton).after(this.#pinnedLabel(group)))

        this.overflowRows.textContent = ""
        overflow.forEach(group => this.overflowRows.appendChild(this.#overflowRow(group)))

        this.overflowCount.textContent = overflow.length
        this.overflowButton.classList.toggle("d-none", overflow.length === 0)
        if (overflow.length === 0) this.overflowPanel.classList.add("d-none")
        this.#refreshBadge()
    }

    #sortedGroups() {
        const canonical = Object.keys(this.#labels)
        const rank = list => group => { const i = list.indexOf(group); return i === -1 ? list.length : i }
        const preference = rank(PINNED_PREFERENCE), canon = rank(canonical)
        return [...this.#groups].sort((a, b) => preference(a) - preference(b) || canon(a) - canon(b) || a.localeCompare(b))
    }

    #pinnedToggle(group) {
        const input = document.createElement("input")
        input.type = "checkbox"
        input.className = "btn-check"
        input.id = `quick_filter_${group}`
        input.autocomplete = "off"
        input.checked = this.#visible(group)
        input.dataset.filterGroup = group
        input.dataset.pinned = ""
        input.addEventListener("change", e => this.#filterChanged(e.currentTarget))
        return input
    }

    #pinnedLabel(group) {
        const label = document.createElement("label")
        label.className = "tracker-filter-toggle"
        label.htmlFor = `quick_filter_${group}`
        label.title = this.#label(group)
        label.dataset.pinned = ""
        const img = document.createElement("img")
        img.src = this.#iconUrl(group)
        img.className = "quick-filter-icon"
        label.appendChild(img)
        return label
    }

    #overflowRow(group) {
        const row = document.createElement("label")
        row.className = "tracker-overflow-row"
        const input = document.createElement("input")
        input.type = "checkbox"
        input.className = "form-check-input"
        input.id = `quick_filter_${group}`
        input.autocomplete = "off"
        input.checked = this.#visible(group)
        input.dataset.filterGroup = group
        input.addEventListener("change", e => this.#filterChanged(e.currentTarget))
        const icon = document.createElement("img")
        icon.src = this.#iconUrl(group)
        icon.className = "tracker-overflow-row-icon"
        const text = document.createElement("span")
        text.textContent = this.#label(group)
        row.append(input, icon, text)
        return row
    }

    #filterChanged(input) {
        this.#mapController.updateFilterGroup(input.dataset.filterGroup, input.checked)
        this.#refreshBadge()
    }

    #bulkSet(checked) {
        this.overflowRows.querySelectorAll("input[data-filter-group]").forEach(input => {
            if (input.checked === checked) return
            input.checked = checked
            this.#mapController.updateFilterGroup(input.dataset.filterGroup, checked)
        })
        this.#refreshBadge()
    }

    #refreshBadge() {
        const hidden = this.#sortedGroups().slice(PINNED_COUNT).filter(group => !this.#visible(group)).length
        this.overflowBadge.textContent = hidden
        this.overflowBadge.classList.toggle("d-none", hidden === 0)
    }

    #visible(group) { return this.#mapController.filterGroups[group] !== false }
    #label(group)   { return this.#labels[group] || group }
    #iconUrl(group) { return `https://pics.lsz-b.at/icons/${group}_c.png` }
}
